import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// DEC-1425: resilient code-split loader. Aliased as `lazy` so every existing
// lazy(() => import(...)) call below gains a timeout + one-shot auto-reload
// instead of hanging forever on a stalled mobile chunk fetch (DEC-1423).
import { lazyWithRetry as lazy } from './utils/lazyWithRetry';
import { AuthProvider, useAuth } from './context/AuthContext';
import { isPublicAuthPath } from './utils/publicPaths';
import { BetaAccessProvider, useBetaAccess } from './context/BetaAccessContext';
import { ToastProvider } from './context/ToastContext';
import { OrgProvider } from './context/OrgContext';
import { FeatureFlagsProvider } from './context/FeatureFlagsContext';
import Navbar from './components/Navbar';
import MobileNavigation from './components/MobileNavigation';
// DEC-721: welcome modal — eager-loaded (small, fires immediately on
// redeem so we can't wait for a chunk fetch).
import BetaWelcomeModal from './components/BetaWelcomeModal';
// DEC-1146: the app's only error boundary. Eager — a lazy boundary can't
// catch an error thrown while its own chunk is still loading.
import AppErrorBoundary from './components/AppErrorBoundary';
// DEC-1148: app-wide Feedback widget (beta bug reporting).
import FeedbackWidget from './components/Feedback/FeedbackWidget';

// DEC-460: Lazy-loaded page components for code splitting
const BetaAccessGate = lazy(() => import('./pages/BetaAccessGate'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Solutions = lazy(() => import('./pages/Solutions'));
const Developers = lazy(() => import('./pages/Developers'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Sites = lazy(() => import('./pages/Sites'));
const SiteDetails = lazy(() => import('./pages/SiteDetails'));
const SiteSetup = lazy(() => import('./pages/SiteSetup'));
// DEC-1209 (M5): the payment-authoritative settlement period walk.
const SettlementPeriod = lazy(() => import('./pages/SettlementPeriod'));
const Programs = lazy(() => import('./pages/Programs'));
const OperatorDashboard = lazy(() => import('./pages/OperatorDashboard'));
const ProgramOperations = lazy(() => import('./pages/ProgramOperations'));
const AmericanUtilitySandbox = lazy(() => import('./pages/AmericanUtilitySandbox'));
const POSCheckoutSandbox = lazy(() => import('./pages/POSCheckoutSandbox'));
const ConsumerAppSandbox = lazy(() => import('./pages/ConsumerAppSandbox'));
const ProgramDefinitionSandbox = lazy(() => import('./pages/ProgramDefinitionSandbox'));
const ProgramEnroll = lazy(() => import('./pages/ProgramEnroll'));
// DEC-922: post-breakout enrollment continuation (Option-B hand-off landing).
const ContinueEnroll = lazy(() => import('./pages/ContinueEnroll'));
const MagicLogin = lazy(() => import('./pages/MagicLogin'));
const Profile = lazy(() => import('./pages/Profile'));
const OrganizationSettings = lazy(() => import('./pages/OrganizationSettings'));
const CreateOrganization = lazy(() => import('./pages/CreateOrganization'));
const OrganizationsIndex = lazy(() => import('./pages/OrganizationsIndex'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
// DEC-999: emailed payout-reconnect confirmation landing page (public).
const PayoutReconnectConfirm = lazy(() => import('./pages/PayoutReconnectConfirm'));
const ServiceProviderDashboard = lazy(() => import('./pages/ServiceProviderDashboard'));
const ServiceProviderSiteView = lazy(() => import('./pages/ServiceProviderSiteView'));
const PublicUpload = lazy(() => import('./pages/PublicUpload'));
const VerifyAccount = lazy(() => import('./pages/VerifyAccount'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const About = lazy(() => import('./pages/About'));

// DEC-393: Lazy-loaded onboarding entry point — only needed on first login
const GettingStarted = lazy(() => import('./pages/GettingStarted'));
const TexturePopupCallback = lazy(() => import('./pages/TexturePopupCallback'));
const AdminBetaInvites = lazy(() => import('./pages/AdminBetaInvites'));
const AdminFeatureFlags = lazy(() => import('./pages/AdminFeatureFlags'));
const AdminTeam = lazy(() => import('./pages/AdminTeam'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));

// DEC-649: Operator Enrollment Packet (review pass-through)
const OperatorEnrollmentPacket = lazy(() => import('./pages/OperatorEnrollmentPacket'));

// DEC-920: public "Onboard via Program Page" ledger.
//   UtilityPrograms — /programs/:slug   (public utility program ledger)
// The single-program iframe embed (/embed/programs/:id) is now served as a
// self-contained document by Express (Option A, DEC-921) so it can carry the
// per-utility frame-ancestors CSP — it is NOT a CRA route.
const UtilityPrograms = lazy(() => import('./pages/UtilityPrograms'));
// DEC-1273: direct program link — public requirements page with on-page email
// capture (the placement option for admins who cannot host the widget).
const ProgramDirectEnroll = lazy(() => import('./pages/ProgramDirectEnroll'));

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/welcome" />;
};

const AppRoutes = () => {
  const { isAuthenticated, isConsumer, isOperator, isServiceProvider } = useAuth();

  // Helper to get the correct home page based on user type
  const getHomePage = () => {
    if (isServiceProvider) return <ServiceProviderDashboard />;
    if (isConsumer) return <Dashboard />;
    if (isOperator) return <OperatorDashboard />;
    return <Navigate to="/welcome" />;
  };

  return (
    <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', color: '#6c757d' }}>Loading...</div>}>
    <Routes>
      {/* Public landing page */}
      <Route
        path="/welcome"
        element={!isAuthenticated ? <LandingPage /> : <Navigate to="/" replace />}
      />
      <Route
        path="/solutions"
        element={<Solutions />}
      />
      <Route
        path="/developers"
        element={<Developers />}
      />
      <Route
        path="/sandbox/american-utility"
        element={<AmericanUtilitySandbox />}
      />
      <Route
        path="/sandbox/pos-checkout"
        element={<POSCheckoutSandbox />}
      />
      <Route
        path="/sandbox/consumer-app/*"
        element={<ConsumerAppSandbox />}
      />
      <Route
        path="/sandbox/program-definition"
        element={<ProgramDefinitionSandbox />}
      />
      <Route
        path="/enroll"
        element={<ProgramEnroll />}
      />
      {/* DEC-922: Option-B break-out landing. The embedded EnrollmentBlock opens
          this in a new top-level tab at /enroll/continue?program=<id>&email=<email>.
          Public + beta-gate-whitelisted; the page collects the emailed 6-digit
          code and the server issues a session only after it verifies (DEC-926). */}
      <Route
        path="/enroll/continue"
        element={<ContinueEnroll />}
      />
      {/* dec804: re-engagement magic-login landing. Public + beta-gate-
          whitelisted (see publicPaths.js). Consumes the single-use ?t= token,
          mints the session, then hard-navigates to the user's site. */}
      <Route
        path="/magic"
        element={<MagicLogin />}
      />
      <Route
        path="/login"
        element={<Login />}
      />
      <Route
        path="/verify"
        element={<VerifyAccount />}
      />
      <Route
        path="/forgot-password"
        element={<ForgotPassword />}
      />
      {/* Public terms page — accessible before login and linked from registration */}
      <Route
        path="/terms"
        element={<Terms />}
      />
      {/* Public privacy policy — required for Green Button Connect / ComEd registration */}
      <Route
        path="/privacy"
        element={<Privacy />}
      />
      {/* DEC-1363: public About page, linked from the waitlist gate's nav.
          Static copy only — see the note in pages/About.js about why this is
          the one marketing surface allowed through the beta gate. */}
      <Route
        path="/about"
        element={<About />}
      />
      <Route
        path="/register"
        element={!isAuthenticated ? <Register /> : <Navigate to="/" />}
      />
      {/* Authenticated routes */}
      <Route
        path="/"
        element={isAuthenticated ? getHomePage() : <Navigate to="/welcome" replace />}
      />
      {/* DEC-1384: /dashboard had NO route and there was no catch-all, so it
          rendered an empty page. That is not cosmetic — Stripe returns the
          payout onboarding popup to /dashboard?payout=return, and the code
          that relays the result to the opener and self-closes the popup lives
          in Dashboard.js. With nothing mounted, the relay never ran: the popup
          sat blank on a DECO shell and the parent card span "Connecting…"
          forever, on the one screen where the user has just handed over their
          bank details. SiteSetup also navigates here in four places.
          Same element as "/" so both URLs are the same home surface. */}
      <Route
        path="/dashboard"
        element={isAuthenticated ? getHomePage() : <Navigate to="/welcome" replace />}
      />
      {/* DEC-393: Post-registration onboarding entry point */}
      <Route
        path="/getting-started"
        element={
          <PrivateRoute>
            <GettingStarted />
          </PrivateRoute>
        }
      />
      <Route
        path="/sites"
        element={
          <PrivateRoute>
            <Sites />
          </PrivateRoute>
        }
      />
      <Route
        path="/sites/:id"
        element={
          <PrivateRoute>
            <SiteDetails />
          </PrivateRoute>
        }
      />
      {/* DEC-1209 (M5): settlement period view — flag-gated server-side */}
      <Route
        path="/sites/:siteId/settlement/:period"
        element={
          <PrivateRoute>
            <SettlementPeriod />
          </PrivateRoute>
        }
      />
      {/* DEC-379: Site onboarding setup wizard — /sites/:siteId/setup */}
      <Route
        path="/sites/:siteId/setup"
        element={
          <PrivateRoute>
            <SiteSetup />
          </PrivateRoute>
        }
      />
      <Route
        path="/programs"
        element={
          <PrivateRoute>
            <Programs />
          </PrivateRoute>
        }
      />
      <Route
        path="/programs/:programId/operations"
        element={
          <PrivateRoute>
            <ProgramOperations />
          </PrivateRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        }
      />
      {/* DEC-716: invite-link landing page. Public route — works whether
          the user is signed in (one-click accept) or signed out (opens
          AuthModal with inviteToken threaded through). */}
      <Route path="/accept-invite" element={<AcceptInvite />} />
      {/* DEC-999: payout-reconnect confirmation. PUBLIC on purpose — the
          threat model is a compromised session, so requiring that session to
          confirm would defeat the control, and the user may open the emailed
          link on a device that has never signed in. */}
      <Route path="/payout-reconnect" element={<PayoutReconnectConfirm />} />
      {/* DEC-541: Organization settings */}
      <Route
        path="/organizations"
        element={
          <PrivateRoute>
            <OrganizationsIndex />
          </PrivateRoute>
        }
      />
      <Route
        path="/organizations/new"
        element={
          <PrivateRoute>
            <CreateOrganization />
          </PrivateRoute>
        }
      />
      <Route
        path="/organizations/:orgId/settings"
        element={
          <PrivateRoute>
            <OrganizationSettings />
          </PrivateRoute>
        }
      />
      {/* Service Provider Routes */}
      <Route
        path="/service-provider"
        element={
          <PrivateRoute>
            <ServiceProviderDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/service-provider/sites/:siteId"
        element={
          <PrivateRoute>
            <ServiceProviderSiteView />
          </PrivateRoute>
        }
      />
      {/* Texture popup callback — runs inside popup, posts result to parent.
          The legacy `/auth/texture/callback` full-page-redirect route was
          deleted in DEC-805 along with the OAuth path that fronted it. */}
      <Route
        path="/texture/popup-callback"
        element={<TexturePopupCallback />}
      />
      {/* Public Upload Route (no auth required) */}
      <Route
        path="/upload/:token"
        element={<PublicUpload />}
      />
      {/* DEC-920: public utility program page (no auth). React Router ranks the
          static "/programs" operator route above this dynamic "/programs/:slug",
          so an authenticated operator hitting "/programs" still gets Programs. */}
      <Route
        path="/programs/:slug"
        element={<UtilityPrograms />}
      />
      {/* DEC-1273: direct program link — the plain-hyperlink placement for
          program admins who cannot host the email-collecting widget (PG&E).
          Router ranks the static "operations" segment above this dynamic
          pair, so /programs/:programId/operations is unaffected. */}
      <Route
        path="/programs/:slug/:programId"
        element={<ProgramDirectEnroll />}
      />
      {/* DEC-649: Operator Enrollment Packet — review pass-through */}
      <Route
        path="/operator/enrollments/:id/packet"
        element={
          <PrivateRoute>
            <OperatorEnrollmentPacket />
          </PrivateRoute>
        }
      />
      {/* DEC-585: Beta invite admin (Deco Energy internal-org members only) */}
      <Route
        path="/admin/beta-invites"
        element={<PrivateRoute><AdminBetaInvites /></PrivateRoute>}
      />
      {/* DEC-590: Feature flag admin (Deco Energy internal-org members only) */}
      <Route
        path="/admin/feature-flags"
        element={<PrivateRoute><AdminFeatureFlags /></PrivateRoute>}
      />
      {/* DEC-720: Internal team management (Deco Energy internal-org members only) */}
      <Route
        path="/admin/team"
        element={<PrivateRoute><AdminTeam /></PrivateRoute>}
      />
      {/* DEC-723 follow-up: Consolidated admin surface — single tabbed page
          replacing the three separate links in the navbar dropdown. The legacy
          deep-link routes above stay mounted for back-compat. */}
      <Route
        path="/admin"
        element={<PrivateRoute><AdminSettings /></PrivateRoute>}
      />
      {/* DEC-1384: a real catch-all. Without one, React Router renders NULL for
          an unmatched path — the app chrome with an empty body, which reads as
          a broken app rather than a wrong address, and gives no clue to anyone
          debugging it. That is exactly how /dashboard hid for so long. Send
          authenticated users home and everyone else to the welcome page. */}
      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? '/' : '/welcome'} replace />}
      />
    </Routes>
    </Suspense>
  );
};

// DEC-723: Routes that render even when the beta gate is up.
//
// Sign-in is handled IN-PLACE on the gate via an AuthModal overlay (see
// BetaAccessGate.js) rather than a route navigation — that prevents
// non-beta visitors from seeing the LandingPage's marketing copy +
// scrollable program details, which would amount to "inside access
// without actual access."
//
// Forgot/verify/terms/privacy are still navigation-based and need to be
// reachable for password recovery, email verification links, and legal
// page links from emails.
//
// Intentionally NOT whitelisted: /welcome (would expose the marketing
// landing page to non-beta visitors), /register (new signups must go
// through the waitlist), /accept-invite (an org invite IS a backdoor to
// platform access — we want full control during beta).
// The whitelist itself now lives in utils/publicPaths.js (2026-09-04), shared
// with the api client's 401 interceptor — which used to hard-redirect a
// public-page visitor with a stale token into the GATED /login route, showing
// the PG&E pilot link's visitors the waitlist. The list, the prefix rules, and
// the program-path regexes (now trailing-slash- and case-tolerant, matching
// the router's own matching) are unchanged in intent; see that module's
// comments for the full DEC-920/922/723/1273/1363 history.

// Wrapper to conditionally show navbar
const AppContent = () => {
  const {
    isAuthenticated, isOperator, user,
    loading: authLoading, authLoadFailed, hasStoredToken, retryLoadUser,
  } = useAuth();
  // DEC-1148: the Feedback FAB must clear the mobile tab bar, so it needs the
  // same breakpoint the rest of the shell uses.
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const { hasBetaAccess, loading: betaLoading } = useBetaAccess();
  // Subscribe to location changes so the gate re-evaluates when the user
  // navigates between gated and whitelisted routes (e.g., gate → /login
  // via the "Sign in" link, or back to / after authenticating).
  const location = useLocation();

  // Show loading while checking beta access
  if (betaLoading) {
    return <div className="loading">Loading...</div>;
  }

  // DEC-1243 (PROD incident): wait for the session to resolve before letting
  // the router decide. `isAuthenticated` is `!!user`, and `user` is null
  // until the profile fetch returns — so rendering routes during that window
  // made "/" evaluate as signed-OUT and REDIRECT to /welcome (the marketing
  // page). A redirect is not a harmless flash: it changes the URL, drops the
  // user into a signup surface, and pollutes history. Costs nothing when
  // there is no token — AuthProvider resolves loading immediately in that
  // case, so the gate and public routes are unaffected.
  if (authLoading) {
    return <div className="loading">Loading...</div>;
  }

  // DEC-1243: the user holds a token but we could not confirm the session
  // (network drop / 5xx, already retried). Say so and offer a retry — never
  // fall through to the signed-out marketing page, which reads as "you've
  // been logged out" and invites a redundant re-signup.
  if (authLoadFailed && hasStoredToken && !isAuthenticated) {
    return (
      <div className="loading" style={{ textAlign: 'center', padding: '48px 20px' }}>
        <p style={{ marginBottom: '16px' }}>
          We couldn&apos;t reach DECO to confirm your session. You&apos;re still signed in —
          this is a connection problem.
        </p>
        <button type="button" className="btn btn-primary" onClick={retryLoadUser}>
          Try again
        </button>
      </div>
    );
  }

  // Show beta gate if no access AND not on a whitelisted auth route.
  const onAuthRoute = isPublicAuthPath(location.pathname);
  if (!hasBetaAccess && !onAuthRoute) {
    return <BetaAccessGate />;
  }

  // When the gate is bypassed for an auth route but the user isn't yet
  // authenticated, suppress the navbar — it would expose org-switcher and
  // other authenticated UI to a not-yet-gated visitor.
  const showNavbar = isAuthenticated && hasBetaAccess;

  return (
    <div className="App">
      {showNavbar && <Navbar />}
      <main>
        <AppRoutes />
      </main>
      {showNavbar && (
        <MobileNavigation userType={isOperator ? 'operator' : 'consumer'} />
      )}
      {/* DEC-721: welcome modal — context-driven, renders only when
          BetaAccessContext has a non-null welcomeContext (set on redeem).
          Mounted globally so it appears regardless of landing route. */}
      <BetaWelcomeModal />
      {/* DEC-1148: the Feedback FAB. Gated on the SAME condition as the
          navbar — authenticated AND through the beta gate — so it never
          appears to a visitor who has no account to attach a report to
          (the endpoint is authenticated-only). Consumer and operator both. */}
      {showNavbar && <FeedbackWidget isMobile={isMobile} userEmail={user?.email || null} />}
    </div>
  );
};

function App() {
  return (
    <BetaAccessProvider>
      <ToastProvider>
        <AuthProvider>
          <OrgProvider>
            <FeatureFlagsProvider>
              <Router>
                {/* DEC-1146: the app's only error boundary. Placed INSIDE the
                    providers and Router so the fallback can still use them if
                    it ever needs to, but wrapping AppContent so a render throw
                    anywhere in the route tree is caught here instead of
                    unmounting the whole app to a blank page (React 18's
                    default with no boundary). */}
                <AppErrorBoundary>
                  <AppContent />
                </AppErrorBoundary>
              </Router>
            </FeatureFlagsProvider>
          </OrgProvider>
        </AuthProvider>
      </ToastProvider>
    </BetaAccessProvider>
  );
}

export default App;
